import React from 'react'

import _ from 'lodash'
import moment from 'moment'

import {Alert, Button, Modal, ModalHeader, ModalActions, Badge, Colors, Popup} from '../../src/components/UI/index.js'
import {formSpacing, DatePicker, FormStack} from '../../src/components/UI/forms/index.js'
import Select from './UI/Select.js'
import time from 'server/utils/time.js'
import {Reseller, Route} from 'types/types.js'
import {emit} from 'messenger/socketio/socketEmit.js'
import type {PopupRef} from '../../src/components/UI/overlays/Popup.js'

type SelectRoutePopupProps = {
    reseller: Reseller
}

type SelectRoutePopupState = {
    isOpen: boolean
    loading: boolean
    date: string
    newDate: string
    routes: Record<string, Route>
    selectedRoute: string
    message: string
    error: string
}

export default class SelectRoutePopup extends React.Component<SelectRoutePopupProps, SelectRoutePopupState> {
    initialState: SelectRoutePopupState
    resolve: (value: unknown) => void
    reject: (reason?: any) => void
    popup: PopupRef

    constructor(props) {
        super(props)
        this.initialState = {
            isOpen: false,
            loading: false,
            date: moment().format('YYYY-MM-DD'),
            newDate: moment().format('YYYY-MM-DD'),
            routes: {},
            selectedRoute: '',
            message: '',
            error: ''
        }

        this.state = JSON.parse(JSON.stringify(this.initialState))
    }

    async onClickNewRoute() {
        const {selectedRoute, routes} = this.state

        const routeName = routes[selectedRoute]?.name

        if (!await this.popup.open('Nieuwe route', `Je gaat een nieuwe route maken met dezelfde instellingen als ${routeName}. Weet je zeker dat je door wil gaan?`)) return

        const lastNr = routeName?.match(/(?:-\s*)?(\d+)$/)?.[1]
        const newName = `${routeName.replace(/\s*(?:-\s*)?\d+$/, '')} - ${lastNr ? parseInt(lastNr, 10) + 1 : 2}`

        try {
            const newRouteId = await emit<string>('orders.copyRoute', selectedRoute, newName)
            this.setState({selectedRoute: newRouteId})
            this.confirm()
        } catch (err) {
            this.setError(err.toString())
        } finally {
            this.popup.close()
        }
    }

    open(date, routes, selectedRoute) {
        this.setState({isOpen: true, date, newDate: date, routes, selectedRoute})

        return new Promise((resolve, reject) => {
            this.resolve = resolve
            this.reject = reject
        })
    }

    close() {
        this.setState({isOpen: false}, () => {
            this.setState(this.initialState)
        })
    }

    setMessage(message) {
        this.setState({message, loading: false})
    }

    setError(error) {
        this.setState({error, loading: false})
    }

    confirm() {
        const {newDate, selectedRoute} = this.state

        this.resolve({newDate, newSelectedRoute: selectedRoute})
        this.close()
    }

    render() {
        const {isOpen, date, newDate, routes, selectedRoute, message, error} = this.state
        const {reseller} = this.props

        const hasConceptRoute = Object.values(routes).some((route) => !route.ready && !route.started && !route.completed)

        return (
            <Modal show={isOpen} style={{width: '90%'}} onClose={this.close.bind(this)}>
                <ModalHeader title='Route selecteren' />

                <FormStack style={{marginBottom: formSpacing.rowGap}}>
                    <DatePicker
                        label='Datum'
                        value={newDate}
                        onChange={(event) => this.setState({newDate: 'target' in event && event.target.value})}
                    />

                    {date === newDate && _.keys(routes).length > 0 &&
                        <Select
                            closeButton
                            label='Route'
                            value={selectedRoute}
                            options={Object.values(routes).sort((route) => Number(route.completed)).map((route) => {
                                return {
                                    title: `${route.name}${route.plannedStartTime ? ` (${route.plannedStartTime})` : ''}`,
                                    value: route._id,
                                    ellipsis: false,
                                    append: route.completed ?
                                        <Badge variant='success'>Afgerond</Badge> :
                                        route.started ?
                                            <Badge textColor={Colors.textDark} variant='info'>Onderweg</Badge> :
                                            route.ready ?
                                                <Badge color={Colors.infoPale}>Gereed</Badge> :
                                                <Badge variant='grey'>Concept</Badge>

                                }
                            })}
                            onChange={(event) => this.setState({selectedRoute: event.target.value})}
                            append={routes[selectedRoute]?.completed ?
                                <Badge variant='success'>Afgerond</Badge> :
                                routes[selectedRoute]?.started ?
                                    <Badge textColor={Colors.textDark} variant='info'>Onderweg</Badge> :
                                    routes[selectedRoute]?.ready ?
                                        <Badge color={Colors.infoPale}>Gereed</Badge> :
                                        <Badge variant='grey'>Concept</Badge>
                            }
                        />
                    }
                </FormStack>

                {['Veloyd', 'Velomedi'].includes(reseller?.name) &&
                    <Button
                        variant='outline-white'
                        label='Nieuwe route'
                        icon='mdi mdi-plus'
                        style={{width: '100%', marginTop: 24, marginBottom: 24}}
                        onClick={this.onClickNewRoute.bind(this)}
                        disabled={!(date === newDate && newDate === time.today() && Object.keys(routes).length > 0 && !hasConceptRoute)}
                    />
                }

                {message &&
                    <Alert style={{marginBottom: 6}} variant='success'>{message}</Alert>
                }

                {error &&
                    <Alert style={{marginBottom: 6}} variant='danger'>{error}</Alert>
                }

                <ModalActions>
                    <Button
                        variant='text'
                        onClick={this.close.bind(this)}
                        label='Annuleren'
                    />

                    <Button
                        variant='text'
                        onClick={this.confirm.bind(this)}
                        label='Bevestigen'
                    />
                </ModalActions>

                <Popup ref={(ref) => this.popup = ref} confirmLabel='Doorgaan' />
            </Modal>
        )
    }
}
